import type { ResumedResult } from '@sentinel/api-client';

export type GrantErrorKind = 'expired' | 'other_user' | 'unavailable' | 'cancelled' | 'generic';

/** Maps a failed POST /auth/mcp/step-up-grants to the page state to show. */
export function classifyGrantError(
  status: number | undefined,
  error: string | undefined,
): GrantErrorKind {
  // Only this code means the ticket is gone (expired, already used, or never
  // issued). Other 404s, such as the MCP server being switched off, are not
  // about the ticket and must not hide the confirm button for good.
  if (status === 404 && error === 'mcp_ticket_not_found') return 'expired';
  if (status === 403 && error === 'mcp_ticket_owner_mismatch') return 'other_user';
  // The ticket needs a factor this account does not have; another step-up
  // cannot help.
  if (status === 403 && error === 'mcp_step_up_unavailable') return 'unavailable';
  // The step-up middleware opens the dialog for every step_up_required and
  // retries once the user finishes it. A retry that asks for step-up again
  // comes back as a 502 `step_up_retry_required_again`, which is generic
  // below; a retry refused for another reason (another account's ticket, an
  // impersonated session) keeps its own 403 and code. So a 403
  // step_up_required reaching the page means the dialog was closed (or a
  // federated prompt took over).
  if (status === 403 && error === 'step_up_required') return 'cancelled';
  return 'generic';
}

const GRANTED_KEY_PREFIX = 'mcp-step-up-granted:';

/**
 * Remembers in this browser that the grant POST for `ticket` succeeded, and
 * until when the grant stays valid (`expiresInSecs` from the response). The
 * ticket is deleted on success, so a later POST for it (a reload, a second
 * tab, a lost response) gets 404; while the grant is still valid that 404
 * means "done". Storage can be unavailable (private mode); then the page falls
 * back to the expired message, whose text covers the already-used case.
 */
export function rememberGranted(ticket: string, expiresInSecs: number, now = Date.now()): void {
  try {
    window.localStorage.setItem(
      GRANTED_KEY_PREFIX + ticket,
      String(now + Math.max(0, expiresInSecs) * 1000),
    );
  } catch {
    // Best effort only.
  }
}

/**
 * True when this browser completed the grant POST for `ticket` and that grant
 * has not expired yet. An expired grant reads as not granted, so an old link
 * reopened later shows the expired message instead of "done".
 */
export function wasGranted(ticket: string, now = Date.now()): boolean {
  try {
    const until = Number(window.localStorage.getItem(GRANTED_KEY_PREFIX + ticket));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
}

/** The grant lifetime a successful grant response carries, or 0 if absent. */
export function grantExpiresIn(body: unknown): number {
  const value = (body as { expires_in?: unknown } | null)?.expires_in;
  return typeof value === 'number' && value > 0 ? value : 0;
}

/** True when a federated-step-up replay was this page's grant POST, whatever its outcome. */
export function matchesGrantPost(result: ResumedResult): boolean {
  if (result.method.toUpperCase() !== 'POST') return false;
  let path: string;
  try {
    path = new URL(result.url).pathname;
  } catch {
    path = result.url.split(/[?#]/, 1)[0] ?? result.url;
  }
  return path === '/auth/mcp/step-up-grants';
}

/** True when a federated-step-up replay completed this page's grant POST. */
export function isGrantResumed(result: ResumedResult): boolean {
  if (!result.ok || !matchesGrantPost(result)) return false;
  return (result.body as { granted?: unknown } | null)?.granted === true;
}
